/**
 * Loads page images and hands back a URL usable as an `<img>` src.
 * Cache + retain-set eviction (with a small back-nav LRU) + abort.
 * Prefetch policy (ring buffer / whole-chapter) lives in {@link PrefetchScheduler}.
 */
export class PageLoader {
    #opts;
    #entries = new Map();
    #retained = new Set();
    /** loaded entries outside the retain set, oldest first */
    #lru = [];
    constructor(opts) {
        this.#opts = opts;
    }
    state(index) {
        return this.#entries.get(index)?.state ?? 'idle';
    }
    bytesOf(index) {
        return this.#entries.get(index)?.bytes ?? 0;
    }
    isLoaded(index) {
        return this.#entries.get(index)?.state === 'loaded';
    }
    /** Decoded bitmap for a page (bitmap loading method only). Triggers a load if needed. */
    async getBitmap(index) {
        const existing = this.#entries.get(index);
        if (existing?.bitmap)
            return existing.bitmap;
        await this.get(index).catch(() => undefined);
        return this.#entries.get(index)?.bitmap ?? null;
    }
    get(index) {
        const existing = this.#entries.get(index);
        if (existing?.url)
            return Promise.resolve(existing.url);
        if (existing?.promise)
            return existing.promise;
        const controller = new AbortController();
        const entry = {
            state: 'loading',
            url: undefined,
            objectUrl: false,
            bytes: 0,
            bitmap: undefined,
            promise: undefined,
            controller,
        };
        this.#entries.set(index, entry);
        this.#emit(index, 'loading');
        entry.promise = this.#fetch(index, controller.signal)
            .then(({ url, objectUrl, bytes, bitmap }) => {
            entry.url = url;
            entry.objectUrl = objectUrl;
            entry.bytes = bytes;
            entry.bitmap = bitmap;
            entry.state = 'loaded';
            entry.promise = undefined;
            entry.controller = undefined;
            this.#emit(index, 'loaded');
            if (bytes > 0)
                this.#opts.onBytes?.(index, bytes);
            if (!this.#retained.has(index))
                this.#touchLru(index);
            return url;
        })
            .catch((err) => {
            entry.state = 'error';
            entry.promise = undefined;
            entry.controller = undefined;
            this.#emit(index, 'error');
            throw err;
        });
        return entry.promise;
    }
    /**
     * Declare the pages worth keeping. In-flight loads outside the set are
     * aborted; loaded pages outside the set stay until the LRU overflows.
     */
    retain(indices) {
        this.#retained = new Set(indices);
        this.#lru = this.#lru.filter((i) => !this.#retained.has(i));
        for (const [index, entry] of this.#entries) {
            if (this.#retained.has(index))
                continue;
            if (entry.state === 'loading') {
                entry.controller?.abort();
                this.#drop(index);
            }
            else if (entry.state === 'loaded' && !this.#lru.includes(index)) {
                this.#touchLru(index);
            }
            else if (entry.state === 'error') {
                this.#drop(index);
            }
        }
        this.#trimLru();
    }
    destroy() {
        for (const entry of this.#entries.values()) {
            entry.controller?.abort();
            if (entry.objectUrl && entry.url)
                URL.revokeObjectURL(entry.url);
            entry.bitmap?.close();
        }
        this.#entries.clear();
        this.#retained.clear();
        this.#lru = [];
    }
    #touchLru(index) {
        this.#lru = this.#lru.filter((i) => i !== index);
        this.#lru.push(index);
        this.#trimLru();
    }
    #trimLru() {
        const cap = this.#opts.keepExtra ?? 12;
        while (this.#lru.length > cap) {
            const victim = this.#lru.shift();
            this.#drop(victim);
        }
    }
    #drop(index) {
        const entry = this.#entries.get(index);
        if (!entry)
            return;
        if (entry.objectUrl && entry.url)
            URL.revokeObjectURL(entry.url);
        entry.bitmap?.close();
        this.#entries.delete(index);
        this.#lru = this.#lru.filter((i) => i !== index);
    }
    /** Discard a page's cache entry (in-flight loads aborted) so the next
     *  `get(index)` re-fetches it — used to retry a page that failed. */
    forget(index) {
        this.#entries.get(index)?.controller?.abort();
        this.#drop(index);
    }
    async #fetch(index, signal) {
        const { source, bookId, variant, loadingMethod } = this.#opts;
        const opts = variant ? { variant, signal } : { signal };
        const result = await source.getPage(bookId, index, opts);
        let blob;
        let url;
        let objectUrl;
        if (typeof result === 'string') {
            if (loadingMethod === 'native') {
                return { url: result, objectUrl: false, bytes: 0, bitmap: undefined };
            }
            const res = await fetch(result, { signal });
            if (!res.ok)
                throw new Error(`page ${index}: HTTP ${res.status}`);
            blob = await res.blob();
            url = URL.createObjectURL(blob);
            objectUrl = true;
        }
        else {
            blob = result;
            url = URL.createObjectURL(blob);
            objectUrl = true;
        }
        let bitmap;
        if (loadingMethod === 'bitmap' && typeof createImageBitmap === 'function') {
            bitmap = await createImageBitmap(blob).catch(() => undefined);
        }
        return { url, objectUrl, bytes: blob.size, bitmap };
    }
    #emit(index, state) {
        this.#opts.onState?.(index, state);
    }
}
//# sourceMappingURL=page-loader.js.map